import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { SelectItem } from '../../components/ui/select';
import { FormField } from '../../components/shared/FormField';
import UserSelect from '../../components/shared/UserSelect';
import { User } from '../../services/userService';
import { Vendor } from '../../services/masterService';
import { SelectField } from '../../components/shared/SelectField';
import { useToast } from '../../context/ToastContext';
import cloudAssetService, {
  CloudEnvironment,
  CloudTier,
  CloudResource,
  CloudResourceType,
  CLOUD_PROVIDER_STATE_LABELS,
} from '../../services/cloudAssetService';
import { useCloudProviders } from './useCloudProviders';

interface Department {
  id: number;
  name: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  resource?: CloudResource | null;
  environments: CloudEnvironment[];
  tiers: CloudTier[];
  resourceTypes: CloudResourceType[];
  departments: Department[];
  /** Vendor master (User Story 103) — who invoices the resource. */
  vendors: Vendor[];
  /** Full user records — `UserSelect` searches and displays email too. */
  users: User[];
  lifecycleStatuses: { label: string; value: string }[];
  currencies: string[];
}

type FormState = Record<string, any>;

/** Radix Select has no empty option value, so a cleared optional select
 * round-trips through this sentinel. */
const NONE = '__none__';

const EMPTY: FormState = {
  provider: '',
  providerResourceId: '',
  name: '',
  providerResourceType: '',
  resourceTypeId: '',
  environmentId: '',
  region: '',
  availabilityZone: '',
  providerState: 'unknown',
  departmentId: '',
  businessOwnerId: '',
  technicalOwnerId: '',
  requestedById: '',
  approvedById: '',
  environmentTier: '',
  application: '',
  costCenter: '',
  vendorId: '',
  lifecycleStatus: 'active',
  monthlyCost: '',
  currency: 'INR',
  notes: '',
};

/**
 * Create / edit form for a cloud resource.
 *
 * The two halves of the form mirror the detail view deliberately: what the
 * provider would have told us, and what only we know. With no discovery, both
 * are typed — but keeping them visually separate is what stops the business
 * context being treated as provider truth.
 */
export const CloudResourceFormDialog: React.FC<Props> = ({
  open,
  onClose,
  onSaved,
  resource,
  environments,
  tiers,
  resourceTypes,
  departments,
  vendors,
  users,
  lifecycleStatuses,
  currencies,
}) => {
  const { showToast } = useToast();
  const { providers } = useCloudProviders();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(resource);

  /** Same retention rule as environments below, applied to the tier master. */
  const selectableTiers = useMemo(() => {
    const currentValue = resource?.environmentTier ?? null;
    return tiers.filter(
      (tier) => tier.isActive !== false || tier.value === currentValue,
    );
  }, [tiers, resource]);

  /** Same retention rule again, applied to the vendor master (User Story 103):
   * a resource billed by a since-retired vendor must keep showing it. */
  const selectableVendors = useMemo(() => {
    const currentId = resource?.vendorId ?? null;
    return vendors.filter(
      (vendor) => vendor.isActive !== false || vendor.id === currentId,
    );
  }, [vendors, resource]);

  /**
   * Only active environments are selectable (User Story 94), plus whichever one
   * this resource is already in — a resource assigned to a since-retired
   * environment must still show what it is assigned to. Without the retained
   * entry Radix finds no matching option, renders the field blank, and the next
   * save silently clears a value nobody meant to touch.
   */
  const selectableEnvironments = useMemo(() => {
    const currentId = resource?.environmentId ?? null;
    return environments.filter(
      (environment) =>
        environment.isActive !== false || environment.id === currentId,
    );
  }, [environments, resource]);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (resource) {
      setForm({
        ...EMPTY,
        ...Object.fromEntries(
          Object.keys(EMPTY).map((key) => [
            key,
            (resource as any)[key] ?? EMPTY[key],
          ]),
        ),
      });
    } else {
      // Providers are master data now — start on the first one configured
      // rather than assuming "aws" exists.
      setForm({ ...EMPTY, provider: providers[0]?.code ?? '' });
    }
  }, [open, resource, providers]);

  const set = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!String(form.provider).trim()) next.provider = 'Provider is required';
    if (!String(form.name).trim()) next.name = 'Name is required';
    if (!String(form.providerResourceId).trim()) {
      next.providerResourceId = 'Provider Resource ID is required';
    }
    if (form.monthlyCost !== '' && Number(form.monthlyCost) < 0) {
      next.monthlyCost = 'Cost cannot be negative';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;

    // Empty selects come back as '' — send undefined so the column stays NULL
    // rather than being coerced to 0 by the DTO's Number transform.
    const payload: Record<string, any> = {};
    Object.entries(form).forEach(([key, value]) => {
      if (value === '' || value === null) return;
      payload[key] = value;
    });

    // The owner and vendor pickers are clearable, so an emptied one has to reach
    // the server as an explicit null — omitting it from the payload would leave
    // the previous value in place and make the clear option look broken.
    // Verified safe against the DTO's Number transform in `dto/cloud.dto.spec.ts`.
    if (isEdit) {
      ([
        'businessOwnerId',
        'technicalOwnerId',
        'requestedById',
        'approvedById',
        'vendorId',
      ] as const).forEach((key) => {
        if (form[key] === '' || form[key] === null) payload[key] = null;
      });
    }

    setSaving(true);
    try {
      if (isEdit && resource) {
        await cloudAssetService.updateResource(resource.id, payload);
        showToast('Cloud resource updated', 'success');
      } else {
        await cloudAssetService.createResource(payload);
        showToast('Cloud resource created', 'success');
      }
      onSaved();
      onClose();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to save cloud resource', 'error');
      if (error?.fieldErrors) setErrors(error.fieldErrors);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      {/* Fixed header/footer, scrolling body — the shared big-form shell. */}
      <DialogContent className="flex flex-col gap-0 overflow-hidden p-0 sm:max-w-3xl sm:p-0">
        <DialogHeader className="shrink-0 border-b px-6 py-4">
          <DialogTitle>
            {isEdit ? 'Edit Cloud Resource' : 'Add Cloud Resource'}
          </DialogTitle>
          <DialogDescription>
            Every field here is typed by hand — nothing is read back from the
            provider.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-8 overflow-y-auto px-6 py-5">
          <section>
            <h3 className="mb-4 border-b pb-2 text-sm font-semibold uppercase tracking-wider text-primary">
              Resource Information
            </h3>
            <div className="grid gap-6 sm:grid-cols-2">
              <FormField
                label="Provider"
                required
                error={errors.provider}
                id="cloud-provider"
              >
                <SelectField
                  value={form.provider}
                  onValueChange={(value) => set('provider', value)}
                  placeholder="Select provider"
                >
                  {providers.map((option) => (
                    <SelectItem key={option.code} value={option.code}>
                      {option.name}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              <FormField
                label="Provider Resource ID"
                required
                error={errors.providerResourceId}
                hint="The id the provider gives this resource"
                id="cloud-resource-id"
              >
                <Input
                  id="cloud-resource-id"
                  value={form.providerResourceId}
                  onChange={(e) => set('providerResourceId', e.target.value)}
                  placeholder="i-0abc123def4567890"
                />
              </FormField>

              <FormField label="Name" required error={errors.name} id="cloud-name">
                <Input
                  id="cloud-name"
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  placeholder="prod-web-01"
                />
              </FormField>

              <FormField label="Resource Type" id="cloud-type">
                <SelectField
                  value={form.resourceTypeId ? String(form.resourceTypeId) : ''}
                  onValueChange={(value) =>
                    set('resourceTypeId', value === NONE ? '' : value)
                  }
                  placeholder="Untyped"
                >
                  <SelectItem value={NONE}>Untyped</SelectItem>
                  {resourceTypes.map((type) => (
                    <SelectItem key={type.id} value={String(type.id)}>
                      {type.normalizedType}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              <FormField
                label="Provider Resource Type"
                hint="Provider-native type or size, kept as metadata"
                id="cloud-native-type"
              >
                <Input
                  id="cloud-native-type"
                  value={form.providerResourceType}
                  onChange={(e) => set('providerResourceType', e.target.value)}
                  placeholder="t3.medium"
                />
              </FormField>

              <FormField label="Environment" id="cloud-environment">
                <SelectField
                  value={form.environmentId ? String(form.environmentId) : ''}
                  onValueChange={(value) =>
                    set('environmentId', value === NONE ? '' : value)
                  }
                  placeholder="No environment"
                >
                  <SelectItem value={NONE}>No environment</SelectItem>
                  {selectableEnvironments.map((environment) => (
                    <SelectItem key={environment.id} value={String(environment.id)}>
                      {environment.name}
                      {environment.isActive === false ? ' (Inactive)' : ''}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              <FormField label="Region" id="cloud-region">
                <Input
                  id="cloud-region"
                  value={form.region}
                  onChange={(e) => set('region', e.target.value)}
                  placeholder="ap-south-1"
                />
              </FormField>

              <FormField label="Availability Zone" id="cloud-az">
                <Input
                  id="cloud-az"
                  value={form.availabilityZone}
                  onChange={(e) => set('availabilityZone', e.target.value)}
                  placeholder="ap-south-1a"
                />
              </FormField>

              <FormField
                label="Provider State"
                hint="What the cloud reports — not the ITAM decision"
                id="cloud-state"
              >
                <SelectField
                  value={form.providerState}
                  onValueChange={(value) => set('providerState', value)}
                  placeholder="Select state"
                >
                  {Object.entries(CLOUD_PROVIDER_STATE_LABELS).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ),
                  )}
                </SelectField>
              </FormField>
            </div>
          </section>

          <section>
            <h3 className="mb-4 border-b pb-2 text-sm font-semibold uppercase tracking-wider text-primary">
              ITAM Information
            </h3>
            <div className="grid gap-6 sm:grid-cols-2">
              <FormField
                label="Department"
                hint="Determines who can see this resource"
                id="cloud-department"
              >
                <SelectField
                  value={form.departmentId ? String(form.departmentId) : ''}
                  onValueChange={(value) =>
                    set('departmentId', value === NONE ? '' : value)
                  }
                  placeholder="No department"
                >
                  <SelectItem value={NONE}>No department</SelectItem>
                  {departments.map((department) => (
                    <SelectItem key={department.id} value={String(department.id)}>
                      {department.name}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              <FormField
                label="Lifecycle Status"
                hint="The ITAM decision about this resource"
                id="cloud-lifecycle"
              >
                <SelectField
                  value={form.lifecycleStatus}
                  onValueChange={(value) => set('lifecycleStatus', value)}
                  placeholder="Select lifecycle status"
                >
                  {lifecycleStatuses.map((status) => (
                    <SelectItem key={status.value} value={status.value}>
                      {status.label}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              <FormField label="Business Owner" id="cloud-business-owner">
                <UserSelect
                  id="cloud-business-owner"
                  users={users}
                  value={form.businessOwnerId}
                  onChange={(userId) => set('businessOwnerId', userId ?? '')}
                  placeholder="Select owner..."
                  clearable
                />
              </FormField>

              <FormField label="Technical Owner" id="cloud-technical-owner">
                <UserSelect
                  id="cloud-technical-owner"
                  users={users}
                  value={form.technicalOwnerId}
                  onChange={(userId) => set('technicalOwnerId', userId ?? '')}
                  placeholder="Select owner..."
                  clearable
                />
              </FormField>

              <FormField
                label="Requested By"
                hint="Who asked for this resource"
                id="cloud-requested-by"
              >
                <UserSelect
                  id="cloud-requested-by"
                  users={users}
                  value={form.requestedById}
                  onChange={(userId) => set('requestedById', userId ?? '')}
                  placeholder="Select requester..."
                  clearable
                />
              </FormField>

              <FormField
                label="Approved By"
                hint="Who authorised it"
                id="cloud-approved-by"
              >
                <UserSelect
                  id="cloud-approved-by"
                  users={users}
                  value={form.approvedById}
                  onChange={(userId) => set('approvedById', userId ?? '')}
                  placeholder="Select approver..."
                  clearable
                />
              </FormField>

              <FormField label="Environment Tier" id="cloud-environment-tier">
                <SelectField
                  value={form.environmentTier || ''}
                  onValueChange={(value) =>
                    set('environmentTier', value === NONE ? '' : value)
                  }
                  placeholder="No tier"
                >
                  <SelectItem value={NONE}>No tier</SelectItem>
                  {selectableTiers.map((tier) => (
                    <SelectItem key={tier.id} value={tier.value}>
                      {tier.label}
                      {tier.isActive === false ? ' (Inactive)' : ''}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>


              <FormField label="Application" id="cloud-application">
                <Input
                  id="cloud-application"
                  value={form.application}
                  onChange={(e) => set('application', e.target.value)}
                  placeholder="Billing Platform"
                />
              </FormField>

              <FormField label="Cost Center" id="cloud-cost-center">
                <Input
                  id="cloud-cost-center"
                  value={form.costCenter}
                  onChange={(e) => set('costCenter', e.target.value)}
                  placeholder="IT-DEPT"
                />
              </FormField>

              {/* Sits beside Cost Center and Monthly Cost because it answers the
                  same commercial question: who sends the invoice. It is not the
                  provider — a reseller or MSP bills plenty of AWS estates. */}
              <FormField
                label="Vendor"
                hint="Who invoices this resource, if not the provider directly"
                id="cloud-vendor"
              >
                <SelectField
                  value={form.vendorId ? String(form.vendorId) : ''}
                  onValueChange={(value) =>
                    set('vendorId', value === NONE ? '' : value)
                  }
                  placeholder="No vendor"
                >
                  <SelectItem value={NONE}>No vendor</SelectItem>
                  {selectableVendors.map((vendor) => (
                    <SelectItem key={vendor.id} value={String(vendor.id)}>
                      {vendor.name}
                      {vendor.isActive === false ? ' (Inactive)' : ''}
                    </SelectItem>
                  ))}
                </SelectField>
              </FormField>

              {/* Currency and amount are one control, matching the Unit Cost
                  field in Consumable Inventory. They were two separate fields,
                  which read as two independent facts — but a cost figure is
                  meaningless without the currency it was billed in, so they
                  belong under one label. */}
              <FormField
                label="Monthly Cost"
                error={errors.monthlyCost}
                hint="As billed. This figure is only as current as this edit."
                id="cloud-cost"
              >
                <div className="flex gap-2">
                  <select
                    className="flex h-10 w-24 shrink-0 rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={form.currency}
                    onChange={(e) => set('currency', e.target.value)}
                    aria-label="Currency"
                  >
                    {currencies.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </select>
                  <Input
                    id="cloud-cost"
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.monthlyCost}
                    onChange={(e) => set('monthlyCost', e.target.value)}
                    placeholder="0.00"
                  />
                </div>
              </FormField>
            </div>

            <FormField label="Notes" id="cloud-notes">
              <Textarea
                id="cloud-notes"
                value={form.notes}
                onChange={(e) => set('notes', e.target.value)}
                rows={3}
              />
            </FormField>
          </section>
          </div>

          <DialogFooter className="mx-0 mt-0 shrink-0 gap-2 border-t bg-background px-6 py-3 sm:gap-0 sm:border-t sm:bg-background sm:px-6 sm:py-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="min-w-[140px]">
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Resource'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CloudResourceFormDialog;
